// Veröffentlichungsplan, Hotel-Edition B1 (PLAN.md §7.3): posts approved in the weekly plan that are not yet
// handed to the planner ("Freigegeben, wird eingeplant") — also while publishing is held. Delete with
// confirmation only while the server allows it (editability.canDelete); afterwards the hand-over answers 409.
import { useState } from "react"
import { CheckCircle, Trash2 } from "lucide-react"
import { FaLinkedin, FaFacebook, FaInstagram } from "react-icons/fa"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { MEDIA_FORMAT_LABELS, effectiveMediaFormat } from "@shared/pillarFormats"
import { LOCK_HINT, useHotelPostMutations, type Editability } from "./hotelPosts"

export interface ApprovedPendingPost {
  id: string
  platform: string
  postTitle: string | null
  postContent: string
  mediaFormat?: string | null
  version: number
  editability: Editability
}

const ICONS = { linkedin: FaLinkedin, instagram: FaInstagram, facebook: FaFacebook } as const

export function ApprovedPendingList({ posts, onOpen }: { posts: ApprovedPendingPost[]; onOpen: (post: ApprovedPendingPost) => void }) {
  const [confirm, setConfirm] = useState<ApprovedPendingPost | null>(null)
  const { remove } = useHotelPostMutations(() => setConfirm(null))

  return (
    <div className="divide-y" data-testid="approved-pending-list">
      {posts.map((post) => {
        const Icon = ICONS[post.platform as keyof typeof ICONS] ?? FaInstagram
        const lock = !post.editability.canDelete ? LOCK_HINT[post.editability.reason] : undefined
        return (
          <div key={post.id} className="flex items-start gap-3 py-3" data-testid={`approved-pending-${post.id}`}>
            <Icon className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
            <button type="button" className="flex-1 min-w-0 text-left" onClick={() => onOpen(post)}>
              <div className="flex items-center gap-2 mb-0.5">
                <Badge variant="outline" className="text-xs gap-1 text-green-700 border-green-300">
                  <CheckCircle className="h-2.5 w-2.5" /> Freigegeben, wird eingeplant
                </Badge>
                <span className="text-xs text-muted-foreground">{MEDIA_FORMAT_LABELS[effectiveMediaFormat(post)]}</span>
              </div>
              <p className="text-sm font-medium truncate">{post.postTitle || post.postContent.split("\n")[0]}</p>
              {lock && <p className="text-xs text-muted-foreground mt-0.5">{lock}</p>}
            </button>
            {post.editability.canDelete && (
              <Button
                size="sm" variant="ghost" className="text-red-600"
                onClick={() => setConfirm(post)}
                data-testid={`delete-approved-${post.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Löschen
              </Button>
            )}
          </div>
        )
      })}

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Beitrag löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Beitrag ist freigegeben, aber noch nicht eingeplant. Nach dem Löschen wird er nicht veröffentlicht
              und auch nicht erneut vorgeschlagen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirm && remove.mutate({ id: confirm.id, version: confirm.version })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-approved"
            >
              {remove.isPending ? "Löscht…" : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
